/**
 * GT-ADMIN-MONITORING-ALERTS-READONLY-MR-03-ADMIN-UI-01 — typed
 * read-only API client for the Admin Console Monitoring & Alerts
 * page.
 *
 * Wraps exactly one backend endpoint (registered in MR-02 /
 * PR #687):
 *
 *   GET /v1/admin/data-plane/monitoring/summary
 *
 * Source-guard contract (`source-guards.test.ts`):
 *   - this module calls only `apiGet`, never `apiSend`;
 *   - the only path is `/v1/admin/data-plane/monitoring/summary`;
 *   - no fixture import;
 *   - no import from `services/**`;
 *   - no `fetch` / `XMLHttpRequest` / raw `axios`;
 *   - no Prometheus / Alertmanager URL, no port literal, no
 *     wg-overlay IP, no upstream label leakage.
 */

import { apiGet, ApiError } from "../lib/api.js";
import type {
  MonitoringErrorCode,
  MonitoringErrorEnvelope,
  MonitoringSummaryDto,
  VpsFleetDto,
} from "./types.js";

const SUMMARY_PATH = "/v1/admin/data-plane/monitoring/summary";
const VPS_FLEET_PATH = "/v1/admin/data-plane/monitoring/vps-fleet";

/**
 * Discriminated-union result the page consumes. The API layer
 * returns either the sanitised summary (`ok: true`) or the closed
 * envelope (`ok: false`). Both shapes are typed; the
 * `transportError` shape is added for browser-side network failures
 * (no response, fetch threw, etc.) so the page can render a
 * specific recovery message instead of an opaque "error".
 */
export type MonitoringSummaryResult =
  | { readonly ok: true; readonly summary: MonitoringSummaryDto }
  | {
      readonly ok: false;
      readonly kind: "envelope";
      readonly envelope: MonitoringErrorEnvelope;
      readonly httpStatus: number;
      readonly requestId: string | undefined;
    }
  | {
      readonly ok: false;
      readonly kind: "disabled";
      readonly httpStatus: 404;
      readonly requestId: string | undefined;
    }
  | {
      readonly ok: false;
      readonly kind: "forbidden";
      readonly httpStatus: 403;
      readonly requestId: string | undefined;
    }
  | {
      readonly ok: false;
      readonly kind: "transport_error";
      readonly message: string;
    };

/**
 * Fetch the monitoring summary. Maps every failure onto the
 * discriminated union above so the page does not have to know
 * about `ApiError` / network throws.
 *
 * NEVER mutates anything; only the documented GET path is sent.
 */
export async function fetchMonitoringSummary(
  signal?: AbortSignal,
): Promise<MonitoringSummaryResult> {
  try {
    const summary = await apiGet<MonitoringSummaryDto>(SUMMARY_PATH, undefined, signal);
    return { ok: true, summary };
  } catch (err) {
    if (err instanceof ApiError) {
      const { status, body, requestId } = err;
      if (status === 404) {
        return { ok: false, kind: "disabled", httpStatus: 404, requestId };
      }
      if (status === 403) {
        return { ok: false, kind: "forbidden", httpStatus: 403, requestId };
      }
      // 502 / 503 / 501: the closed `MonitoringErrorEnvelope` lives
      // in the body. If the body matches the envelope shape, surface
      // it verbatim; otherwise synthesise a generic upstream
      // envelope so the page can still render a sanitised message.
      if (isMonitoringErrorEnvelope(body)) {
        return { ok: false, kind: "envelope", envelope: body, httpStatus: status, requestId };
      }
      return {
        ok: false,
        kind: "envelope",
        envelope: synthesiseEnvelopeForStatus(status),
        httpStatus: status,
        requestId,
      };
    }
    // Network throw (fetch failure, abort, etc.).
    const message = err instanceof Error ? err.message : "monitoring summary fetch failed";
    return { ok: false, kind: "transport_error", message };
  }
}

/**
 * GT-INFRA-RESILIENCE-VPS-HEALTH — fetch the read-only VPS fleet
 * health. Same discriminated-union contract and failure mapping as
 * `fetchMonitoringSummary`; the browser only ever sends the fixed
 * `VPS_FLEET_PATH` GET (no upstream URL, no PromQL, no raw labels).
 */
export type VpsFleetResult =
  | { readonly ok: true; readonly fleet: VpsFleetDto }
  | {
      readonly ok: false;
      readonly kind: "envelope";
      readonly envelope: MonitoringErrorEnvelope;
      readonly httpStatus: number;
      readonly requestId: string | undefined;
    }
  | { readonly ok: false; readonly kind: "disabled"; readonly httpStatus: 404 }
  | { readonly ok: false; readonly kind: "forbidden"; readonly httpStatus: 403 }
  | { readonly ok: false; readonly kind: "transport_error"; readonly message: string };

export async function fetchVpsFleet(signal?: AbortSignal): Promise<VpsFleetResult> {
  try {
    const fleet = await apiGet<VpsFleetDto>(VPS_FLEET_PATH, undefined, signal);
    return { ok: true, fleet };
  } catch (err) {
    if (err instanceof ApiError) {
      const { status, body, requestId } = err;
      if (status === 404) return { ok: false, kind: "disabled", httpStatus: 404 };
      if (status === 403) return { ok: false, kind: "forbidden", httpStatus: 403 };
      if (isMonitoringErrorEnvelope(body)) {
        return { ok: false, kind: "envelope", envelope: body, httpStatus: status, requestId };
      }
      return {
        ok: false,
        kind: "envelope",
        envelope: synthesiseEnvelopeForStatus(status),
        httpStatus: status,
        requestId,
      };
    }
    const message = err instanceof Error ? err.message : "vps fleet fetch failed";
    return { ok: false, kind: "transport_error", message };
  }
}

/* ── Helpers (pure) ────────────────────────────────────────────── */

function isMonitoringErrorEnvelope(body: unknown): body is MonitoringErrorEnvelope {
  if (typeof body !== "object" || body === null) return false;
  const rec = body as Record<string, unknown>;
  if (rec["ok"] !== false) return false;
  const code = rec["code"];
  const message = rec["message"];
  if (typeof code !== "string" || typeof message !== "string") return false;
  const VALID_CODES: ReadonlyArray<MonitoringErrorCode> = [
    "monitoring_unreachable",
    "upstream_timeout",
    "upstream_invalid_response",
    "not_configured",
  ];
  return (VALID_CODES as ReadonlyArray<string>).includes(code);
}

function synthesiseEnvelopeForStatus(status: number): MonitoringErrorEnvelope {
  if (status === 502) {
    return {
      ok: false,
      code: "upstream_invalid_response",
      message: "monitoring upstream returned an unexpected response",
    };
  }
  if (status === 503) {
    return {
      ok: false,
      code: "monitoring_unreachable",
      message: "monitoring upstream is unreachable",
    };
  }
  return {
    ok: false,
    code: "monitoring_unreachable",
    message: "monitoring upstream did not respond",
  };
}

/** Exported for tests so the source-guard can pin the literal. */
export const MONITORING_SUMMARY_PATH = SUMMARY_PATH;

/** Exported for tests so the source-guard can pin the literal. */
export const MONITORING_VPS_FLEET_PATH = VPS_FLEET_PATH;
